import { type Common } from './common.js';
import {
  type CreateOptions,
  type DashboardEmbedPopulatedWithSecret,
  type DashboardEmbedPopulatedWithoutSecret,
  type UpdateOptions,
} from './models/dashboard/dashboardEmbed.js';
import type * as DashboardTemplate from './models/dashboard/dashboardTemplate.js';
import { type DashboardType } from './models/dashboard/types.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';

interface FindAllOptions {
  organizationId?: string;
  standard?: boolean;
  page?: number;
  limit?: number;
  dashboardType?: DashboardType;
}

export class API {
  common: Common;
  baseEndpoint = '/dashboard-embeds';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options?: FindAllOptions): Promise<DashboardEmbedPopulatedWithoutSecret[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<DashboardEmbedPopulatedWithoutSecret>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  getProxyUrl(id: string): string {
    return this.common.constructPath(`${this.baseEndpoint}/${id}/proxy`);
  }

  async findOneById(id: string): Promise<DashboardEmbedPopulatedWithoutSecret> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async removeOneById(id: string): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }

  async findAllTemplates(options?: FindAllOptions): Promise<DashboardTemplate.DashboardTemplate[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/templates`, options);
    return await this.common.getJSON(url);
  }

  async create(options: CreateOptions): Promise<DashboardEmbedPopulatedWithSecret> {
    const url = this.common.constructPath(`${this.baseEndpoint}`);
    return await this.common.postJSON(url, options);
  }

  async update(id: string, options: UpdateOptions): Promise<DashboardEmbedPopulatedWithSecret> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, options);
  }
}
